import * as trackingCategoriesApi from '@/api/TrackingCategoriesApi';
import DDLogs from '@/DDLogs';
import router from '@/router';
import { PerformanceMarks, performanceService } from '@/utils/PerformanceService';
import { DateLocales } from '@riseupil/common-utils';
import reactiveCashflow from '@riseupil/reactive-cashflow';
import _ from 'lodash';
import moment from 'moment';
import Vue from 'vue';
import CashflowViewApi from '../../api/CashflowViewApi';
import * as HamsterApi from '../../api/HamsterApi';
import LatestSeenTransactionDateApi from '../../api/LatestSeenTransactionCreationDateApi';
import cashflowViewConsts from '../../constants/cashflow-view';
import cashflowHistory from '../utilities/cashflow-history';
import cashflowNormalizer from '../utilities/cashflow-normalizer';

const state = {
  presentedBudgetDate: null,
  allCashflows: [],
  oldestAvailableBudgetDate: null,
  cashflowUITree: {},
  cashflow: {},
  finishLoadingAllBudgets: false,
  lastActivity: null,
  trackingCategoriesOrder: [],
  cashflowStartDay: cashflowViewConsts.DEFAULT_CASHFLOW_START_DAY,
  includedAccountsInFilter: [],
};

const EXPIRATION_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

function transactionsInCurrentCashflow(state) {
  const { envelopes, excluded, variableTransactions, variableIncomeTransactions } = state.cashflow;
  return _.chain(envelopes).flatMap(envelope => envelope.actuals).concat(excluded).concat(variableTransactions)
    .concat(variableIncomeTransactions)
    .value();
}

function normalizeTrackingCategories(trackingCategories) {
  return _.map(trackingCategories, category => ({
    categoryId: category.categoryId,
    categoryName: cashflowViewConsts.CATEGORY_NAMES.TRACKING,
    expense: category.categoryName,
    cashflowCategory: cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING,
    icon: category.icon,
    label: category.categoryName,
    pluralLabel: category.categoryName,
    style: cashflowViewConsts.CATEGORY_STYLE[cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING],
  }));
}

const getters = {
  allCategories: (state, getters) => [...cashflowViewConsts.CATEGORIES, ...normalizeTrackingCategories(getters.trackingCategories)],
  currentBudgetDate: () => cashflowHistory.getCurrentBudgetDate(),
  presentedBudget: state => _.find(state.allCashflows, { budgetDate: state.presentedBudgetDate }),
  allBudgetDates: state => _getAllBudgetDates(state),
  lastBudgetDate: state => _.last(_getAllBudgetDates(state)),
  predictedBalanceAtEndOfMonth: state => (state.cashflow.predictedBalance === 0 ? 0 : -1 * state.cashflow.predictedBalance),
  riseupGoal: state => state.cashflow.balancedRiseupGoal,
  isGoalCustom: state => !_.isNil(state.cashflow.riseupGoalEnvelope.details.riseupGoalInputAmount),
  budgetUpdatedAt: state => state.cashflow.lastUpdatedAt,
  totalIncome: state => state.cashflow.variableIncomeSum + state.cashflow.incomeEnvelopesActualSum,
  totalExpense: state => state.cashflow.variableActualSum + state.cashflow.expenseEnvelopesActualSum,
  filteredTotalIncome: state => state.cashflow.filteredVariableIncomeSum + state.cashflow.filteredIncomeEnvelopesActualSum,
  filteredTotalExpense: state => state.cashflow.filteredVariableActualSum + state.cashflow.filteredExpenseEnvelopesActualSum,
  monthName: state => moment(state.presentedBudgetDate).locale('he').format('MMM'),
  monthFullName: state => moment(state.presentedBudgetDate).locale('he').format('MMMM'),
  balance: state => -1 * state.cashflow.balance,
  cashflowGraphHistory: (state, getters) => _.chain(state.allCashflows)
    .thru(cashflows => cashflowHistory.getCashflowsForPastMonthsGraph(cashflows, state.presentedBudgetDate, getters.currentBudgetDate))
    .map(budget => {
      const cashflow = reactiveCashflow.createCashflow(budget, DateLocales.EN_IL);
      return {
        cashflowDate: budget.budgetDate,
        total: -1 * cashflow.balance,
      };
    })
    .value(),
  pastCashflowStatus: (state, getters) => {
    const previousBudgetDate = cashflowHistory.getPrevBudgetDate(getters.currentBudgetDate);
    return _.chain(state.allCashflows)
      .thru(cashflows => cashflowHistory.getCashflowsForEnvelopeTrend(cashflows, previousBudgetDate, 6))
      .map(budget => {
        const cashflow = reactiveCashflow.createCashflow(budget, DateLocales.EN_IL);
        return {
          cashflowDate: budget.budgetDate,
          isPositive: cashflow.balance < 0,
        };
      })
      .value();
  },
  excluded: state => state.cashflow.excluded,
  hasExcluded: state => cashflowNormalizer.visibleExcluded(state.cashflow.excluded).length > 0,
  isPresentingCurrentMonth: (state, getters) => state.presentedBudgetDate === getters.currentBudgetDate,
  isPresentingLastMonth: (state, getters) => state.presentedBudgetDate === getters.lastBudgetDate,
  isPresentingFutureMonth: (state, getters, rootState, rootGetters) => {
    const futureMonthNavigation = rootGetters['featureFlags/futureMonthNavigation'];
    if (!futureMonthNavigation) { return false; }
    const nextMonth = moment(getters.currentBudgetDate).add(1, 'month').format('YYYY-MM');
    return state.presentedBudgetDate === nextMonth;
  },
  monthsAhead: (state, getters) => {
    const diff = moment(state.presentedBudgetDate).diff(moment(getters.currentBudgetDate), 'months');
    return diff > 0 ? diff : undefined;
  },
  trackingCategories: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.trackingCategory],
  fixedIncomeUiCategories: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.income].fixedIncome.categories,
  fixedExpenseUiCategories: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.fixedExpense].categories,
  fixedSavingUiCategories: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.fixedSaving].categories,
  oneTimeSavingCategory: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.oneTimeSaving][0],
  weeks: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.variableExpense].weekly.realExpenses.categories,
  currentWeek: (state, getters) => _.find(getters.weeks, w => w.timeFrameDetails.isCurrentWeek),
  remainingWeeklyVariables: (state, getters) => getters.currentWeek?.remaining ?? 0,
  remainingMonthlyVariables: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.variableExpense].recommendedOrExpectedAmount,
  hasNextWeek: (state, getters) => (getters.currentWeek ? getters.currentWeek.weekIndex < getters.weeks.length - 1 : false),
  nextWeekRemainingVariables: (state, getters) => (getters.hasNextWeek ? getters.weeks[getters.currentWeek.weekIndex + 1].remaining : 0),
  fixedExpenseCategories: state => state.cashflow.fixedTree.fixedExpenseEnvelopes.map(e => e.details.expense),
  fixedIncomeCategories: state => state.cashflow.fixedTree.fixedIncomeEnvelopes.map(e => e.details.expense),
  transactionsInCurrentCashflow,
  allExcludedTransactions: state => _.chain(state.allCashflows)
    .flatMap(cashflow => cashflow.excluded)
    .keyBy('transactionId')
    .value(),
  isFilterApplied: state => !_.isEmpty(state.includedAccountsInFilter),
  allSequences: state => _.chain(state.allCashflows).flatMap(cashflow => cashflow.envelopes)
    .filter(env => (env.type === 'fixed' && env.actuals.length !== 0))
    .flatMap(env => env.actuals)
    .groupBy('sequenceId')
    .value(),
  allAvailableBudgetDates: (state, getters, rootState, rootGetters) => {
    const startDate = moment(state.oldestAvailableBudgetDate, 'YYYY-MM');
    const futureMonthNavigation = rootGetters['featureFlags/futureMonthNavigation'];

    const endDate = futureMonthNavigation
      ? moment(getters.currentBudgetDate, 'YYYY-MM').add(1, 'month')
      : moment(getters.currentBudgetDate, 'YYYY-MM');

    const monthsDiff = endDate.diff(startDate, 'months') + 1;

    return _.times(monthsDiff, i => startDate.clone().add(i, 'months').format('YYYY-MM'));
  },

  past3MonthsCasfhlows: (state, getters) => _.chain(state.allCashflows)
    .filter(cf => cf.budgetDate < getters.currentBudgetDate)
    .orderBy('budgetDate', 'desc')
    .take(3)
    .value(),

  pastMonthsVariableAvg: (state, getters) => _.chain(getters.past3MonthsCasfhlows)
    .map(cf =>
      _.chain(cf.envelopes)
        .filter(e => e.type === 'variable')
        .map(e => _.sumBy(e.actuals, 'billingAmount'))
        .mean()
        .value())
    .mean()
    .value(),

  pastMonthsTrackingCategoriesAvg: (state, getters) => _.chain(getters.past3MonthsCasfhlows)
    .flatMap(cf => cf.envelopes.map(envelope => ({ ...envelope, budgetDate: cf.budgetDate })))
    .filter(e => e.type === 'trackingCategory')
    .groupBy('details.trackingCategory.name')
    .mapValues(group => {
      const isWeekly = group[0].details.trackingCategory.frequency === 'weekly';
      if (isWeekly) {
        // For weekly categories, first aggregate by month, then calculate average
        const monthlyTotals = _.chain(group)
          .groupBy('budgetDate')
          .mapValues(monthlyGroup => _.sumBy(monthlyGroup, envelope => _.sumBy(envelope.actuals, 'billingAmount')))
          .values()
          .value();
        return _.mean(monthlyTotals);
      }
      return _.mean(group.map(tc => Number(_.sumBy(tc.actuals, 'billingAmount'))));
    })
    .map((amount, name) => ({ name: name === 'כלכלה' ? 'סופר' : name, amount }))
    .value(),

  pastMonthsTrackingCategoriesExcludingSavingsAvg: (state, getters) => {
    const currentCashflow = _.find(state.allCashflows, cf => cf.budgetDate === getters.currentBudgetDate);
    if (!currentCashflow) {
      return [];
    }
    const pastAvgByName = _.keyBy(getters.pastMonthsTrackingCategoriesAvg, 'name');
    return _.chain(currentCashflow.envelopes)
      .filter(e => e.type === 'trackingCategory')
      .reject(e => e.details.trackingCategory.trackingCategoryType === 'saving')
      .reject(e => e.details.trackingCategory.name === '__saving-hidden-category__')
      .groupBy(e => {
        const rawName = e.details.trackingCategory.name;
        return rawName === 'כלכלה' ? 'סופר' : rawName;
      })
      .map((envelopes, name) => ({
        name,
        amount: pastAvgByName[name]?.amount ?? _.sumBy(envelopes, e => e.predictedAmount ?? 0),
      }))
      .value();
  },

  pastMonthsSavingsCategoriesAvg: (state, getters) => _.chain(getters.pastMonthsTrackingCategoriesAvg)
    .filter(c => c.name === '__saving-hidden-category__')
    .value(),

  lastFixedExpenses: (state, getters) => {
    const currentCashflow = _.find(state.allCashflows, cf => cf.budgetDate === getters.currentBudgetDate);
    if (!currentCashflow) {
      return [];
    }

    return _.chain(currentCashflow.envelopes)
      .filter(e => e.type === 'fixed' && !e.details.isIncome)
      .filter(e => !_.some(e.details.sequence, item =>
        item.paymentNumber === item.totalNumberOfPayments - 1))
      .groupBy(e => e.details.expense)
      .map((group, expense) => ({
        name: expense,
        amount: _.sumBy(group, e => (e.actuals?.length ? e.actuals[0].billingAmount : e.balancedAmount)),
        envelopes: group,
      }))
      .sortBy(c => c.name)
      .value();
  },

  lastFixedExpensesExcludeSavings: (state, getters) => _.reject(getters.lastFixedExpenses, e => e.name === 'השקעה וחיסכון'),

  lastFixedSavingsExpenses: (state, getters) => _.filter(getters.lastFixedExpenses, e => e.name === 'השקעה וחיסכון'),

  lastFixedIncomesTotal: (state, getters) => {
    const currentCashflow = _.find(state.allCashflows, cf => cf.budgetDate === getters.currentBudgetDate);
    if (!currentCashflow) {
      return 0;
    }

    return _.chain(currentCashflow.envelopes)
      .filter(e => e.type === 'fixed' && e.details.isIncome)
      .sumBy(e => Math.abs((e.actuals?.length ? e.actuals[0].incomeAmount : e.balancedAmount)))
      .value();
  },
  expensesPredictedAmounts: state => {
    const { cashflowUITree } = state;
    const { cashflowTypes } = cashflowViewConsts;

    const getMaxAmount = item => Math.max(item.recommendedOrExpectedAmount || 0, item.upToNowAmount || 0);

    const sumItem = (type, item) => {
      if (!item) {
        return 0;
      }

      // Variable expense has no prediction at the moment so we take the upToNowAmount
      // To add the amount that was spent to the general prediction
      if (type === cashflowTypes.variableExpense) {
        return item.upToNowAmount || 0;
      }

      if (Array.isArray(item)) {
        return item.length > 0 ? _.sumBy(item, getMaxAmount) : 0;
      }

      return getMaxAmount(item);
    };

    const sumType = type => {
      const item = cashflowUITree[type];
      return sumItem(type, item);
    };

    return sumType(cashflowTypes.fixedExpense)
           + sumType(cashflowTypes.fixedSaving)
           + sumType(cashflowTypes.oneTimeSaving)
           + sumType(cashflowTypes.trackingCategory)
           + sumType(cashflowTypes.variableExpense);
  },
};

const actions = {
  async initBudgets({ commit, dispatch, state }, { budgetDate }) {
    performanceService.markStart(PerformanceMarks.INIT_BUDGETS);
    performanceService.markStart(PerformanceMarks.INIT_CURRENT_BUDGET);
    const apiBudget = await cashflowHistory.getBudget(budgetDate);
    const budgetToDisplayFirst = getBudgetWithRecentChanges(apiBudget);

    commit('setCashflowStartDay', budgetToDisplayFirst.cashflowStartDay);
    if (state.allCashflows.length === 0) {
      commit('setCashflows', [budgetToDisplayFirst]);
      await dispatch('updatePresentedBudgetDate', budgetToDisplayFirst.budgetDate);
    }
    dispatch('loadAllBudgets');
    dispatch('fetchOldestBudgetDate');
    performanceService.markEnd(PerformanceMarks.INIT_CURRENT_BUDGET);
  },
  async initCashflowStartDay({ commit }) {
    const cashflowStartDay = await CashflowViewApi.getCashflowStartDay();
    commit('setCashflowStartDay', cashflowStartDay);
  },
  async loadAllBudgets({ state, commit, dispatch, getters, rootGetters }) {
    DDLogs.info('loadAllBudgets', { presentedBudgetDate: state.presentedBudgetDate });
    if (!state.presentedBudgetDate) {
      return;
    }
    performanceService.markStart(PerformanceMarks.INIT_PREVIOUS_BUDGETS);

    const budgets = await CashflowViewApi.fetchPreviousBudgets({
      budgetDate: state.presentedBudgetDate,
      numMonthsBack: cashflowViewConsts.MONTHS_BACK_TO_LOAD,
    });
    // Allow budgets up to 1 month in the future only if feature flag is enabled
    const futureMonthNavigation = rootGetters['featureFlags/futureMonthNavigation'];
    const maxAllowedMonth = futureMonthNavigation
      ? moment(getters.currentBudgetDate).add(1, 'month').format('YYYY-MM')
      : getters.currentBudgetDate;
    const relevantBudgets = _.filter(budgets, ({ budgetDate }) => budgetDate <= maxAllowedMonth);
    const currentBudget = getters.currentBudgetDate === getters.presentedBudget.budgetDate
      ? getBudgetWithRecentChanges(budgets.find(b => b.budgetDate === getters.currentBudgetDate))
      : {};
    const relevantBudgetsWithoutCurrent = _.reject(relevantBudgets, b => b.budgetDate === currentBudget.budgetDate);
    const upToNowCashflows = _.isEmpty(currentBudget) ? relevantBudgetsWithoutCurrent : [...relevantBudgetsWithoutCurrent, currentBudget];
    if (!_.isEmpty(upToNowCashflows)) {
      commit('setCashflowStartDay', _.last(upToNowCashflows).cashflowStartDay);
    }
    commit('setCashflows', upToNowCashflows);
    performanceService.markEnd(PerformanceMarks.INIT_PREVIOUS_BUDGETS);

    if (!getters.presentedBudget) {
      const latestBudgetDate = _.max(getters.allBudgetDates);
      await dispatch('updatePresentedBudgetDate', latestBudgetDate);
    }
    commit('setFinishLoadingAllBudgets');
    dispatch('lazyLoadCashflowEnrichment').then(() => dispatch('logLatestSeenTransactionCreationDate'));

    dispatch('cashflowSearch/initCashflowSearch', { allCashflows: upToNowCashflows, plans: rootGetters['planAhead/plans'] }, { root: true });
    performanceService.markEnd(PerformanceMarks.INIT_BUDGETS);
  },
  async updatePresentedBudgetDate({ commit, dispatch, getters }, budgetDate) {
    const parsedBudgetDate = budgetDate === 'current' ? cashflowHistory.getCurrentBudgetDate() : budgetDate;
    commit('setPresentedBudgetDate', parsedBudgetDate);
    await dispatch('resetBudgetTree');
  },
  async resetBudgetTree({ commit, dispatch, getters, state }) {
    const budget = getters.presentedBudget;
    if (budget) {
      const includedAccountsInFilterArray = JSON.parse(JSON.stringify(state.includedAccountsInFilter));
      const cashflow = reactiveCashflow.createCashflow(
        { ...budget, cashflowStartDay: state.cashflowStartDay },
        DateLocales.EN_IL,
        includedAccountsInFilterArray,
      );
      const cashflowUITree = cashflowNormalizer.normalizeCashflow(cashflow, includedAccountsInFilterArray);
      commit('setCashflow', cashflow);
      commit('setCashflowUITree', cashflowUITree);
      await dispatch('insights/reactiveInsights', null, { root: true });
    }
  },
  async moveToSpecificBudgetMonth({ dispatch, commit, state, getters }, { budgetDate }) {
    if (getters.allAvailableBudgetDates.includes(budgetDate) && !getters.allBudgetDates.includes(budgetDate)) {
      await dispatch('loadBudgetDate', budgetDate);
    }
    await dispatch('backfillBudgetDates', { budgetDate, includeCurrent: true });
    await router.push({ params: { budgetDate }, query: router.currentRoute.query });
  },
  async moveToNextBudgetMonth({ dispatch }) {
    const nextBudgetDate = cashflowHistory.getNextBudgetDate(state.presentedBudgetDate);
    await dispatch('moveToSpecificBudgetMonth', { budgetDate: nextBudgetDate });
  },
  async moveToPrevBudgetMonth({ dispatch, state }) {
    const prevBudgetDate = cashflowHistory.getPrevBudgetDate(state.presentedBudgetDate);
    await dispatch('moveToSpecificBudgetMonth', { budgetDate: prevBudgetDate });
  },
  async loadBudgetDate({ dispatch, commit, getters, rootGetters }, budgetDate) {
    if (!getters.allBudgetDates.includes(budgetDate) && moment(budgetDate).isSameOrAfter(cashflowViewConsts.GOOD_CASHFLOWS_START)) {
      DDLogs.info('loadBudgetDate', { budgetDate });
      const budget = await CashflowViewApi.fetchBudget({ budgetDate });
      const budgetWithPending = await dispatch('createTrackingCategory/applyPendingCategoriesToCashflow', { cashflow: budget }, { root: true });
      await commit('setCashflows', _.sortBy([...state.allCashflows, budgetWithPending], 'budgetDate'));
      dispatch('cashflowSearch/addTransactionsForSearch', {
        newCashflows: [budgetWithPending],
        plans: rootGetters['planAhead/plans'],
      }, { root: true });
    }
  },
  async backfillBudgetDates(
    { dispatch, commit, getters, rootGetters, state },
    { budgetDate, includeCurrent = false, monthsBack = cashflowViewConsts.MONTHS_BACK_TO_LOAD },
  ) {
    const necessaryBudgetDates = _.times(monthsBack, i => moment(budgetDate).subtract(i + 1, 'months').format('YYYY-MM'));
    if (includeCurrent) {
      necessaryBudgetDates.push(budgetDate);
    }

    const validNecessaryDates = _.intersection(necessaryBudgetDates, getters.allAvailableBudgetDates);
    // allBudgetDates has budget dates that are already loaded in memory
    const budgetsToLoad = _.difference(validNecessaryDates, getters.allBudgetDates);
    if (_.isEmpty(budgetsToLoad)) {
      return;
    }
    const budgetDateToLoadFrom = _.max(budgetsToLoad);
    const numMonthsBack = validNecessaryDates.length - _.indexOf(validNecessaryDates, budgetDateToLoadFrom) - 1;
    DDLogs.info('backfillBudgetDates', { budgetDateToLoadFrom, budgetsToLoad });
    const budgets = await CashflowViewApi.fetchPreviousBudgets({ budgetDate: budgetDateToLoadFrom, numMonthsBack });
    const budgetsWithPending = await Promise.all(budgets.map(cashflow =>
      dispatch('createTrackingCategory/applyPendingCategoriesToCashflow', { cashflow }, { root: true })));
    await commit('setCashflows', _.sortBy([...budgetsWithPending, ...state.allCashflows], 'budgetDate'));
    dispatch('cashflowSearch/addTransactionsForSearch', { newCashflows: budgetsWithPending, plans: rootGetters['planAhead/plans'] }, { root: true });
  },
  logLatestSeenTransactionCreationDate({ getters }) {
    if (router.currentRoute.query.incognito) {
      return;
    }
    const latestSeenTransactionCreationDate = _.maxBy(getters.transactionsInCurrentCashflow, t => t.createdAt)?.createdAt;
    const isPresentingCurrenCashflowMonth = getters.presentedBudget.budgetDate === getters.currentBudgetDate;
    if (latestSeenTransactionCreationDate && isPresentingCurrenCashflowMonth) {
      LatestSeenTransactionDateApi.setLatestSeenTransactionCreationDate(latestSeenTransactionCreationDate);
    }
  },
  async lazyLoadCashflowEnrichment({ dispatch, commit }) {
    const [savingTransactionIds, latestSeenTransactionCreationDate, { order }] = await Promise.all([
      HamsterApi.getCustomerSavingTransactions(),
      LatestSeenTransactionDateApi.getLatestSeenTransactionCreationDate(),
      trackingCategoriesApi.getTrackingCategoriesDisplaySettings(),
    ]);
    cashflowNormalizer.setSavingTransactions(savingTransactionIds.map(t => t.transactionId));
    cashflowNormalizer.setCategoriesOrder(order);
    if (latestSeenTransactionCreationDate) {
      cashflowNormalizer.setLatestSeenTransactionCreationDate(latestSeenTransactionCreationDate);
    }
    commit('setTrackingCategoriesOrder', order);
    dispatch('resetBudgetTree');
  },
  setTrackingCategoriesOrder({ commit }, trackingCategoriesOrder) {
    commit('setTrackingCategoriesOrder', trackingCategoriesOrder);
    cashflowNormalizer.setCategoriesOrder(trackingCategoriesOrder);
  },
  setFilter({ commit, dispatch }, includedAccounts) {
    commit('setIncludedAccountsInFilter', includedAccounts);
    dispatch('resetBudgetTree');
  },
  async fetchOldestBudgetDate({ commit }) {
    const oldestBudgetDate = await CashflowViewApi.fetchOldestBudgetDate();
    commit('setOldestAvailableBudgetDate', oldestBudgetDate);
    return oldestBudgetDate;
  },
};

function _getAllBudgetDates(state) {
  return state.allCashflows.map(({ budgetDate }) => budgetDate);
}

function getBudgetsFromStorage() {
  const storedBudgets = localStorage.getItem('riseup.budgets');
  return storedBudgets ? JSON.parse(storedBudgets) : {};
}

/**
 * Saves only the current budget to localStorage, overwriting any previous data.
 * This ensures we only keep the most recent budget in storage.
 *
*/
function saveBudgetToStorage(budgetDate, budget, version, expirationTime) {
  const budgetData = {
    [budgetDate]: {
      budget,
      version,
      expirationTime,
    },
  };
  try {
    localStorage.setItem('riseup.budgets', JSON.stringify(budgetData));
  } catch (error) {
    DDLogs.info('Failed to save budget to localStorage:', { error, budgetDate, objectSize: getObjectSizeUTF8(budgetData) });
  }
}

function getObjectSizeUTF8(obj) {
  const jsonString = JSON.stringify(obj);
  const sizeInBytes = new TextEncoder().encode(jsonString).length;
  return sizeInBytes;
}
/**
 * Removes the entire budgets object from localStorage.
 * Since we only store the current budget, we remove everything when clearing.
 */
function removeBudgetFromStorage() {
  localStorage.removeItem('riseup.budgets');
}

function getBudgetWithRecentChanges(apiBudget) {
  const versionCreated = apiBudget.created;
  const budgets = getBudgetsFromStorage();
  const budgetWithRecentChanges = budgets[apiBudget.budgetDate];
  if (!budgetWithRecentChanges) {
    return apiBudget;
  }

  if (versionCreated === budgetWithRecentChanges.version && !isStoredDataExpired(budgetWithRecentChanges)) {
    DDLogs.info('Using stored budget', { budgetDate: apiBudget.budgetDate, versionCreated, storedVersion: budgetWithRecentChanges.version });
    return budgetWithRecentChanges.budget;
  }

  if (versionCreated !== budgetWithRecentChanges.version) {
    DDLogs.info('Using api version budget', { budgetDate: apiBudget.budgetDate, versionCreated, storedVersion: budgetWithRecentChanges.version });
  }

  // If the stored budget is expired, we remove it and use the running version.
  removeBudgetFromStorage();
  return apiBudget;
}

function isStoredDataExpired(budgetWithRecentChanges) {
  return Date.now() > budgetWithRecentChanges.expirationTime;
}

// mutations
const mutations = {
  setCashflowOptimistic(state, newCashflow) {
    const index = _.findIndex(state.allCashflows, { budgetDate: state.presentedBudgetDate });
    // use Vue.set to update arrays and induce reactivity
    Vue.set(state.allCashflows, index, newCashflow);
    const version = newCashflow.created;
    const expirationTime = Date.now() + EXPIRATION_TIME;
    saveBudgetToStorage(state.presentedBudgetDate, newCashflow, version, expirationTime);
  },
  setCashflowForBudgetDate(state, { budgetDate, cashflow }) {
    const index = _.findIndex(state.allCashflows, { budgetDate });
    if (index !== -1) {
      Vue.set(state.allCashflows, index, cashflow);
    }
  },
  setCashflows(state, allCashflows) {
    state.allCashflows = allCashflows;
  },
  setCashflow(state, cashflow) {
    state.cashflow = cashflow; //eslint-disable-line
  },
  setCashflowUITree(state, cashflowUITree) {
    state.cashflowUITree = cashflowUITree;
  },
  setPresentedBudgetDate(state, budgetDate) {
    state.presentedBudgetDate = budgetDate;
  },
  setFinishLoadingAllBudgets(state) {
    state.finishLoadingAllBudgets = true;
  },
  setCashflowStartDay(state, cashflowStartDay) {
    state.cashflowStartDay = cashflowStartDay;
  },
  setTrackingCategoriesOrder(state, trackingCategoriesOrder) {
    state.trackingCategoriesOrder = trackingCategoriesOrder;
  },
  setIncludedAccountsInFilter(state, includedAccountsInFilter) {
    state.includedAccountsInFilter = includedAccountsInFilter;
  },
  setOldestAvailableBudgetDate(state, oldestAvailableBudgetDate) {
    state.oldestAvailableBudgetDate = oldestAvailableBudgetDate;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
